// WebAuthn (passkey) ceremony helpers. We convert the server's JSON options into
// the ArrayBuffer-typed structures navigator.credentials expects, then serialize
// the resulting credential back to base64url JSON for the /verify endpoints.
//
// Server option shape follows the WebAuthn JSON spec (challenge/id fields are
// base64url strings). If the server returns fully-formed PublicKeyCredential*JSON,
// browsers with `PublicKeyCredential.parseCreationOptionsFromJSON` can skip the
// manual conversion — we feature-detect and fall back to manual decoding.

import { b64urlDecode, b64urlEncode } from './base64.js';

export function webauthnSupported(): boolean {
  return typeof window !== 'undefined' &&
    !!window.PublicKeyCredential &&
    !!navigator.credentials;
}

export async function platformAuthenticatorAvailable(): Promise<boolean> {
  try {
    return webauthnSupported() &&
      (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable());
  } catch {
    return false;
  }
}

function decodeCreationOptions(json: any): PublicKeyCredentialCreationOptions {
  return {
    ...json,
    challenge: b64urlDecode(json.challenge),
    user: { ...json.user, id: b64urlDecode(json.user.id) },
    excludeCredentials: (json.excludeCredentials || []).map((c: any) => ({
      ...c,
      id: b64urlDecode(c.id),
    })),
  };
}

function decodeRequestOptions(json: any): PublicKeyCredentialRequestOptions {
  return {
    ...json,
    challenge: b64urlDecode(json.challenge),
    allowCredentials: (json.allowCredentials || []).map((c: any) => ({
      ...c,
      id: b64urlDecode(c.id),
    })),
  };
}

function encodeAttestation(cred: PublicKeyCredential) {
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: b64urlEncode(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: b64urlEncode(r.clientDataJSON),
      attestationObject: b64urlEncode(r.attestationObject),
      transports: r.getTransports ? r.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  };
}

function encodeAssertion(cred: PublicKeyCredential) {
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: b64urlEncode(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: b64urlEncode(r.clientDataJSON),
      authenticatorData: b64urlEncode(r.authenticatorData),
      signature: b64urlEncode(r.signature),
      userHandle: r.userHandle ? b64urlEncode(r.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  };
}

/** Run the WebAuthn create ceremony. Returns the JSON to POST to register/verify. */
export interface CeremonyOpts {
  // Called once if we detect the document isn't focused and begin auto-waiting
  // for it to return (so the UI can show a gentle, non-error hint). Passed
  // `false` again once focus is regained / before the ceremony proceeds.
  onWaitingForFocus?: (waiting: boolean) => void;
  // Max time to wait for focus to come back before giving up. Default 20s so a
  // user's natural next interaction completes signup without any explicit retry.
  focusTimeoutMs?: number;
}

export async function createPasskey(optionsJson: any, opts: CeremonyOpts = {}): Promise<any> {
  const publicKey = decodeCreationOptions(optionsJson.publicKey ?? optionsJson);
  await ensureDocumentFocused(opts);
  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error('Passkey creation was cancelled.');
  return encodeAttestation(cred);
}

// WebAuthn ceremonies REQUIRE the document to have focus, or the browser throws
// `NotAllowedError: The document is not focused`. Because we do async work
// (username check, DPoP keygen, registerBegin) between the click and the
// ceremony, focus can be lost (e.g. the user tabbed away, a popup/devtools stole
// focus, or the window blurred).
//
// Recovery is fully automatic and requires NO explicit "try again" click:
//   1. Try to reclaim focus programmatically (window.focus()) and give it a
//      short moment — handles transient blurs with zero user action.
//   2. If still unfocused, arm one-shot listeners and RESOLVE the instant the
//      document regains focus OR the user's next natural interaction lands
//      (pointerdown/keydown/visibility). Any of these refocuses the tab, so the
//      ceremony then proceeds transparently on that same gesture.
//   3. Only if nothing happens within focusTimeoutMs do we throw
//      DocumentNotFocusedError (the UI's last-resort message).
async function ensureDocumentFocused(opts: CeremonyOpts = {}): Promise<void> {
  if (typeof document === 'undefined' || document.hasFocus()) return;

  // Phase 1: programmatic reclaim + brief settle (handles transient blur).
  try { window.focus(); } catch { /* ignore */ }
  if (await waitForFocus(400)) return;

  // Phase 2: wait for focus or the next user interaction (no click on our
  // button required — ANY tap/keypress anywhere refocuses and resolves).
  opts.onWaitingForFocus?.(true);
  try {
    const ok = await waitForFocus(opts.focusTimeoutMs ?? 20000, true);
    if (!ok) throw new DocumentNotFocusedError();
  } finally {
    opts.onWaitingForFocus?.(false);
  }
}

// Resolve true as soon as the document is focused (optionally also treating a
// user interaction as the trigger, since interacting refocuses the tab), or
// false on timeout.
function waitForFocus(timeoutMs: number, watchInteraction = false): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    if (document.hasFocus()) { resolve(true); return; }
    let settled = false;
    const cleanup = () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      if (watchInteraction) {
        window.removeEventListener('pointerdown', onInteract, true);
        window.removeEventListener('keydown', onInteract, true);
      }
      clearTimeout(timer);
    };
    const finish = (v: boolean) => { if (settled) return; settled = true; cleanup(); resolve(v); };
    const check = () => { try { window.focus(); } catch { /* ignore */ } if (document.hasFocus()) finish(true); };
    const onFocus = () => finish(true);
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    // On interaction, defer one microtask+tick so focus settles before we check.
    const onInteract = () => setTimeout(check, 0);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    if (watchInteraction) {
      window.addEventListener('pointerdown', onInteract, true);
      window.addEventListener('keydown', onInteract, true);
    }
    const timer = setTimeout(() => finish(false), timeoutMs);
  });
}

// Distinct error type so the UI can show a tailored "tap the page" hint.
export class DocumentNotFocusedError extends Error {
  constructor() {
    super('This browser tab needs focus to create a passkey. Click anywhere on the page, then try again.');
    this.name = 'DocumentNotFocusedError';
  }
}

/**
 * Run the WebAuthn get ceremony. Pass `conditional:true` for discoverable-credential
 * autofill (Flow B — the platform offers stored passkeys with zero typing).
 */
export async function getPasskeyAssertion(
  optionsJson: any,
  opts: { conditional?: boolean; signal?: AbortSignal } & CeremonyOpts = {},
): Promise<any> {
  const publicKey = decodeRequestOptions(optionsJson.publicKey ?? optionsJson);
  const req: CredentialRequestOptions = { publicKey, signal: opts.signal };
  if (opts.conditional) (req as any).mediation = 'conditional';
  // Conditional (autofill) UI is allowed to run without focus; only guard the
  // explicit modal ceremony, which requires a focused document.
  if (!opts.conditional) await ensureDocumentFocused(opts);
  const cred = (await navigator.credentials.get(req)) as PublicKeyCredential | null;
  if (!cred) throw new Error('Passkey login was cancelled.');
  return encodeAssertion(cred);
}
